import { sanitizeUrl } from "../private/sanitizeUrl";

declare const __XVIEW_PERF_VERSION__: string;

type Fields = Record<string, string | number | boolean | null>;

interface ErrorDetails {
  name: string;
  message: string;
  stack?: string;
  cause?: ErrorDetails;
}

function errorText(value: unknown): string {
  try {
    return String(value)
      .replace(/\b(?:https?|wss?):\/\/[^\s<>"']+/gi, (url) => {
        try {
          // Preserve stack frame coordinates after removing URL credentials.
          const suffix = url.match(/:\d+:\d+\)?$/)?.[0] ?? "";
          const address = suffix ? url.slice(0, -suffix.length) : url;
          return sanitizeUrl(new URL(address).href) + suffix;
        } catch {
          return "[Invalid URL]";
        }
      })
      .replace(/[a-f0-9]{64}/gi, "[redacted-token]");
  } catch {
    return "[Unprintable error value]";
  }
}

function errorProperty(error: object, key: string): unknown {
  try {
    return Reflect.get(error, key);
  } catch {
    return "[Unreadable error property]";
  }
}

function describeError(
  error: unknown,
  seen = new Set<unknown>(),
  depth = 0,
): ErrorDetails {
  if (depth >= 8) return { name: "Error", message: "[Cause depth limit]" };
  if (seen.has(error)) return { name: "Error", message: "[Circular cause]" };
  if (
    error === null ||
    (typeof error !== "object" && typeof error !== "function")
  )
    return { name: typeof error, message: errorText(error) };
  seen.add(error);
  const name = errorProperty(error, "name");
  const message = errorProperty(error, "message");
  const stack = errorProperty(error, "stack");
  const cause = errorProperty(error, "cause");
  return {
    name: typeof name === "string" ? errorText(name) : "Error",
    message: errorText(message === undefined ? error : message),
    ...(typeof stack === "string" ? { stack: errorText(stack) } : {}),
    ...(cause === undefined
      ? {}
      : { cause: describeError(cause, seen, depth + 1) }),
  };
}

export function captureErrorLog(
  event: string,
  error: unknown,
  fields: Fields = {},
) {
  try {
    if (window.__xviewCaptureDebug !== true) return;
    const details = describeError(error);
    captureLog(event, {
      ...fields,
      errorName: details.name,
      errorMessage: details.message,
      errorStack: details.stack ?? null,
      // Keep the existing flat, JSON-serializable startup log format.
      errorCause: details.cause ? JSON.stringify(details.cause) : null,
    });
  } catch {
    captureLog(event, {
      ...fields,
      errorMessage: "[Error details unavailable]",
    });
  }
}

declare global {
  interface Window {
    /** Opt in before loading the SDK to enable capture diagnostics. */
    __xviewCaptureDebug?: boolean;
    __xviewCaptureLogs?: Fields[];
  }
}

export function captureLog(event: string, fields: Fields = {}) {
  try {
    if (window.__xviewCaptureDebug !== true) return;
    const record = {
      at: Date.now(),
      version:
        typeof __XVIEW_PERF_VERSION__ === "string"
          ? __XVIEW_PERF_VERSION__
          : "development",
      event,
      ...fields,
    };
    // vConsole may load after the SDK. Keep a bounded, redacted startup history.
    const history = (window.__xviewCaptureLogs ??= []);
    history.push(record);
    if (history.length > 100) history.splice(0, history.length - 100);
    console.info("[xview-capture]", record);
  } catch {
    /* Diagnostics must never break the host page. */
  }
}

export async function captureStep<T>(
  name: string,
  run: () => Promise<T>,
): Promise<T> {
  const started = Date.now();
  captureLog(`${name}.started`);
  try {
    const result = await run();
    captureLog(`${name}.completed`, { elapsedMs: Date.now() - started });
    return result;
  } catch (error) {
    captureErrorLog(`${name}.failed`, error, {
      elapsedMs: Date.now() - started,
    });
    throw error;
  }
}
